import { instance } from '../api/api';
import { toApiError } from '../utils/apiError';
import { ITechList, IAutopark, ICar } from '../types/type';
import { formatDateForApi } from '../utils/date.utils';

export interface TechListResponse {
  data: TechListItem[];
  page: number;
  totalCount: number;
  totalPages: number;
}

// Новый интерфейс для ответа от /api/techlist
export interface NewTechListResponse {
  data: NewTechListItem[];
  page: number;
  totalCount: number;
  totalPages: number;
}

// Новый интерфейс для элементов из /api/techlist
export interface NewTechListItem {
  autopark: {
    actualAddress: string;
    balance: number;
    bankName: string;
    bik: string;
    currentAccount: string;
    email: string;
    externalID: string;
    fullnameEmployee: string;
    fullnameOwner: string;
    id: string;
    index: string;
    inn: string;
    kpp: string;
    legalAddress: string;
    legalName: string;
    ogrn: string;
    orgType: string;
    phone: string;
    regionCode: number;
    tradeName: string;
  };
  body: boolean;
  car: {
    autonegotiation: boolean;
    autonegotiationTechnikID: string;
    autoparkID: string;
    carImages: {
      carID: string;
      createdAt: string;
      id: string;
      photoType: string;
      photoUrl: string;
    }[];
    carType: string;
    createdAt: string;
    diagnosticCardNumber: number;
    diagnosticCardPhoto: string;
    diagnosticCardVerified: boolean;
    driverID: string;
    id: string;
    issueDate: string;
    mark: string;
    model: string;
    number: string;
    osagoNumber: string;
    osagoPhoto: string;
    rentExpiredAt: string;
    taxiLicense: string;
    updatedAt: string;
    validityPeriodOfDC: string;
    validityPeriodOfOsago: string;
  };
  chassis: boolean;
  checkStatus: string;
  checkTime: string;
  engine: boolean;
  id: string;
  num: number;
  odometerPhoto: string;
  odometerReading: number;
  optic: boolean;
  profile: {
    address: string;
    autoparkIDs: string[];
    avatar: string;
    driverID: string;
    email: string;
    firstName: string;
    id: string;
    isActive: boolean;
    isInternal: boolean;
    lastName: string;
    loginDate: string;
    password: string;
    phone: string;
    registeredAt: string;
    role: string;
    username: string;
  };
  reasonForRefusal: string;
  technicID: string;
}

export interface TechListItem extends ITechList {
  autopark?: IAutopark;
  car?: {
    autonegotiation: boolean;
    autonegotiationTechnikID: string;
    autoparkID: string;
    mark: string;
    model: string;
    carImages: {
      carID: string;
      createdAt: string;
      id: string;
      photoType: string;
      photoUrl: string;
    }[];
    createdAt: string;
    diagnosticCardNumber: number;
    diagnosticCardPhoto: string;
    diagnosticCardVerified: boolean;
    driverID: string;
    id: string;
    issueDate: string;
    number: string;
    taxiLicense: string;
    updatedAt: string;
    validityPeriodOfDC: string;
    carType?: string;
  };
  profile?: {
    address: string;
    autoparkIDs: string[];
    avatar: string;
    driverID: string;
    email: string;
    firstName: string;
    lastName: string;
    id: string;
    isActive: boolean;
    loginDate: string;
    password: string;
    phone: string;
    registeredAt: string;
    role: string;
    username: string;
  };
  reasonForRefusal?: string;
}

export interface TechListFilters {
  page?: number;
  limit?: number;
  startTime?: string;
  endTime?: string;
  fullName?: string;
  num?: string;
  checkStatus?: string;
  autoparkName?: string;
  mark?: string;
  model?: string;
  number?: string;
}

// Новый интерфейс для фильтров newTO
export interface NewTechListFilters {
  page?: number;
  limit?: number;
  startTime?: string;
  endTime?: string;
  num?: string;
  number?: string;
  autoparkIDs?: string;
  status?: string;
}

export interface CarUpdateData {
  number?: string;
  mark?: string;
  model?: string;
  taxiLicense?: string;
  issueDate?: string;
  diagnosticCardNumber?: number | null;
  validityPeriodOfDC?: string | null;
  diagnosticCardVerified?: boolean;
}

export interface DiagnosticCardVerification {
  verified: boolean;
}

export const TechnicalService = {
  async getTechnicTechLists(technicID: string, filters: TechListFilters = {}): Promise<TechListResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Копируем фильтры и преобразуем даты
      const formattedFilters = { ...filters };

      // Форматируем даты, если они есть
      if (filters.startTime) {
        formattedFilters.startTime = formatDateForApi(filters.startTime);
      }

      if (filters.endTime) {
        formattedFilters.endTime = formatDateForApi(filters.endTime);
      }

      Object.entries(formattedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const { data } = await instance.get<TechListResponse>(`/techlist/technik/${technicID}?${queryParams.toString()}`);

      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить списки технических осмотров');
    }
  },

  // Новая функция для получения данных из /api/techlist
  async getTechList(filters: NewTechListFilters = {}): Promise<NewTechListResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Копируем фильтры и преобразуем даты
      const formattedFilters = { ...filters };

      // Форматируем даты, если они есть
      if (filters.startTime) {
        formattedFilters.startTime = formatDateForApi(filters.startTime);
      }

      if (filters.endTime) {
        formattedFilters.endTime = formatDateForApi(filters.endTime);
      }

      Object.entries(formattedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const { data } = await instance.get<NewTechListResponse>(`/techlist?${queryParams.toString()}`);

      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить списки технических осмотров');
    }
  },

  // Методы для работы с автомобилями
  async updateCar(carId: string, carData: CarUpdateData): Promise<ICar> {
    try {
      const { data } = await instance.put<ICar>(`/cars/${carId}`, carData);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить данные автомобиля');
    }
  },

  async updateCarTechnik(carId: string, technikId: string | null): Promise<ICar> {
    try {
      const { data } = await instance.put<ICar>(`/cars/${carId}/technik?technikID=${technikId}`);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить техника автомобиля');
    }
  },

  async updateCarAutonegotiation(carId: string, technikId: string, status: boolean): Promise<ICar> {
    try {
      const { data } = await instance.put<ICar>(`/cars/${carId}/autonegotiation/${technikId}?status=${status}`);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить статус автосогласования');
    }
  },

  // Обновление значения одометра
  async updateOdometerReading(techListId: string, odometerValue: string, technikID?: string): Promise<any> {
    try {
      const { data } = await instance.put(`/techlist/${techListId}/odometer`, {
        odometerReading: odometerValue,
        technikID,
      });
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить значение одометра');
    }
  },

  // Обновление статуса диагностической карты
  async updateDiagnosticCardVerification(carId: string, verificationData: DiagnosticCardVerification): Promise<ICar> {
    try {
      const { data } = await instance.put<ICar>(`/cars/${carId}/diagnostic-card-verification`, verificationData);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить статус диагностической карты');
    }
  },

  // Обновление статуса технического осмотра
  async updateTechListStatus(
    techListId: string,
    status: 'waiting' | 'completed' | 'rejected' | 'inProgress',

    technikID?: string,
    reasonForRefusal?: string,
  ): Promise<any> {
    try {
      const { data } = await instance.put(`/techlist/${techListId}/status`, {
        status,
        technikID,
        reasonForRefusal,
      });
      return data;
    } catch (error: any) {
      throw toApiError(error, 'Не удалось обновить статус технического осмотра');
    }
  },
};
